<template>
  <div>
    <h3 class="text-static-default-hi heading-3">
      {{ i18n(translations.title) }}
    </h3>
    <div class="text-static-default-hi body-2 mb-24">
      {{ i18n(translations.subtitle) }}
    </div>

    <div class="flex flex-col gap-12">
      <LargePicker
        v-for="(picker, index) in pricePickers"
        :key="picker.price.amount"
        :index
        :label="i18n.price(picker.price, { maximumFractionDigits: 0 })"
        :model-value="picker.price"
        :selected="
          picker.price.amount === modelValue.amount &&
          customAmount === undefined
        "
        :sub-label="picker.label"
        @click="handlePricePickerClick(picker.price)"
      />

      <RevInputNumber
        id="gift-purchase-amount-custom"
        v-model="customAmount"
        class="[&_button]:top-10 [&_input]:!h-[70px] [&_input]:!pt-4"
        :class="{
          '[&_div]:!bg-surface-brand-hi': customAmount !== undefined,
          '[&_input]:!bg-surface-brand-hi': customAmount !== undefined,
        }"
        :error="customAmountError"
        :label="i18n(translations.customLabel)"
        :max="parseFloat(maxPrice.amount)"
        :min="parseFloat(minPrice.amount)"
        :show-stepper="false"
        @update:model-value="handleCustomAmount"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import type { Price } from '@backmarket/http-api'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevInputNumber } from '@ds/components/InputNumber'

import LargePicker from '~/scopes/product/components/CustomizationFunnel/components/LargePicker/LargePicker.vue'

import translations from './PricePickers.translations'

const i18n = useI18n()

const props = defineProps<{
  modelValue: Price
  maxPrice: Price
  minPrice: Price
}>()

const customAmount = ref<number>()
const emit = defineEmits(['update:modelValue'])

function handlePricePickerClick(price: Price) {
  customAmount.value = undefined
  emit('update:modelValue', price)
}

const customAmountError = computed(() => {
  if (customAmount.value === undefined) {
    return ''
  }

  if (
    customAmount.value < parseFloat(props.minPrice.amount) ||
    customAmount.value > parseFloat(props.maxPrice.amount)
  ) {
    return i18n(translations.customAmountError, {
      minPrice: i18n.price(props.minPrice, { maximumFractionDigits: 0 }),
      maxPrice: i18n.price(props.maxPrice, { maximumFractionDigits: 0 }),
    })
  }

  return ''
})

const createPriceFromAmount = (amount: string) => {
  return {
    amount,
    currency: props.modelValue.currency,
  }
}

function handleCustomAmount(value: number) {
  if (value === undefined) {
    emit('update:modelValue', createPriceFromAmount('50'))
  }

  if (
    value > parseFloat(props.minPrice.amount) &&
    value < parseFloat(props.maxPrice.amount)
  ) {
    emit('update:modelValue', createPriceFromAmount(value.toString()))
  }
}

const pricePickers = [
  {
    price: createPriceFromAmount('20'),
    label: i18n(translations.text1),
  },
  {
    price: createPriceFromAmount('50'),
    label: i18n(translations.text2),
  },
  {
    price: createPriceFromAmount('100'),
    label: i18n(translations.text3),
  },
  {
    price: createPriceFromAmount('200'),
    label: i18n(translations.text4),
  },
  {
    price: createPriceFromAmount('500'),
    label: i18n(translations.text5),
  },
]
</script>
